import { DoubleCheckIcon } from 'components/icons/DoubleCheck'
import { useDialogs } from 'hooks/dialogs'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { ShowExperienceDialog } from '../dialogs/ShowExperienceDialog'
import { SummaryDialog } from '../dialogs/SummaryDialog'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { InternalLink } from 'components/core/InternalLink'
import { ChevronRight } from 'components/icons/ChevronRight'
import { MeetingResultsDialog } from '../dialogs/MeetingResultsDialog'
import { PeerReviewViewDialog } from '../dialogs/PeerReviewViewDialog'
import { useTranslationTyped } from 'i18n/i18n'
import { ValidLibraryObjectActionState } from '@breakoutlearning/firebase-repository/stores/ValidLibraryObject'
import { CompletedViewPeerReviewNeeded } from './CompletedViewPeerReviewNeeded'
import { CompletedViewFinishQuizNeeded } from './CompletedViewFinishQuizNeeded'
import { type StudentPeerReviewController } from '@breakoutlearning/firebase-repository/cubits/StudentAssignment/StudentPeerReviewController'
import { useGradeExplanationReadiness } from 'hooks/useGradeExplanationReadiness'

export const CompletedView = observer(function CompletedView({
  actionState,
  peerReviewController,
}: {
  actionState: ValidLibraryObjectActionState
  peerReviewController: StudentPeerReviewController
}) {
  const cubit = useStudentAssignmentCubit()
  const readyGradeExplanations = useGradeExplanationReadiness(
    cubit.repository.featureFlags.data.gradeExplanationAccessEnabled &&
      cubit.assignment.data.showFinalGradeToParticipants === true &&
      Boolean(cubit.roomStateId) &&
      actionState === ValidLibraryObjectActionState.viewResults
  )
  const { t, tt } = useTranslationTyped()
  const { showDialog } = useDialogs()

  const summary = cubit.roomStateSummary
  const canShowNewResults = true

  // Finishing the quiz comes before peer review, which comes before results —
  // getActionState encodes that precedence, so read it rather than re-deriving.
  const isFinishQuizPending =
    actionState === ValidLibraryObjectActionState.finishEndOfSessionQuiz

  const isPeerReviewPending =
    actionState === ValidLibraryObjectActionState.completePeerReviews

  const assignmentTitle = cubit.assignment.slideDeck?.data.slideDeckName ?? ''

  return (
    <>
      {/* the quiz outranks everything below: the results spell out its correct
          answers, so a student who still owes them must not see this view */}
      {isFinishQuizPending && <CompletedViewFinishQuizNeeded />}
      {/* return only peer review component if needed */}
      {!isFinishQuizPending && isPeerReviewPending && (
        <CompletedViewPeerReviewNeeded
          peerReviewController={peerReviewController}
        />
      )}
      {/* else return default view*/}
      {!isFinishQuizPending && !isPeerReviewPending && (
        <div
          className={classNames(
            `mt-1 flex flex-1 flex-col items-center justify-center py-5 text-center`
          )}
        >
          <p className="text-body-medium text-grey-text">
            {t('dart.experience')}
          </p>
          <p className="mt-3 text-title-large">
            <DoubleCheckIcon
              size={20}
              className="mr-1 inline-block align-top text-breakout-dark-green"
            />
            {t('dart.completed')}
          </p>
          <div className="mt-6">
            {cubit.repository.featureFlags.data.gradeExplanationAccessEnabled &&
              cubit.assignment.data.showFinalGradeToParticipants === true &&
              cubit.roomStateId &&
              readyGradeExplanations.has(
                `${cubit.assignmentId}/${cubit.roomStateId}`
              ) &&
              actionState === ValidLibraryObjectActionState.viewResults && (
                <InternalLink
                  to="gradeExplainer"
                  params={{
                    sectionId: cubit.sectionId,
                    assignmentId: cubit.assignmentId,
                    roomStateId: cubit.roomStateId,
                    userId: cubit.repository.uid,
                  }}
                  onClick={() =>
                    cubit.repository.logEvent('grade_explainer_entry_clicked', {
                      source: 'assignment_details',
                      assignment_id: cubit.assignmentId,
                      room_id: cubit.roomStateId,
                    })
                  }
                  className="mb-4 inline-flex items-center text-label-medium underline outline-offset-2"
                >
                  {tt.student_assignment.explain_my_score()}
                  <ChevronRight size={16} />
                </InternalLink>
              )}
            <div className="flex flex-row flex-wrap justify-center gap-2">
              <BreakoutButton
                data-testid="view-experience-button"
                onClick={(e) => {
                  showDialog(
                    () => (
                      <ShowExperienceDialog
                        slideDeckId={cubit.libraryObject.slideDeckId}
                        sectionId={cubit.sectionId}
                        assignmentId={cubit.assignmentId}
                      />
                    ),
                    { trigger: e.currentTarget }
                  )
                }}
                className="inline-block rounded-2xl bg-core-secondary px-6 py-2 text-body-large whitespace-nowrap text-core-on-tertiary"
              >
                {t('student_assignment.view_experience')}
              </BreakoutButton>
              {(summary?.hasData || canShowNewResults) && (
                <BreakoutButton
                  data-testid="view-summary-button"
                  onClick={(e) => {
                    showDialog(
                      () => {
                        if (canShowNewResults)
                          return (
                            <MeetingResultsDialog
                              roomId={cubit.roomStateId!}
                              onStudentAssignmentPage
                            />
                          )

                        return (
                          <SummaryDialog
                            summary={summary.data}
                            roomStateName={cubit.roomState.roomStateName}
                            roomStateActiveSlideChangedAt={
                              cubit.roomState.data.activeSlideChangedAt || null
                            }
                          />
                        )
                      },
                      { trigger: e.currentTarget }
                    )
                  }}
                  className="inline-block rounded-2xl bg-core-secondary px-6 py-2 text-body-large whitespace-nowrap text-core-on-tertiary"
                >
                  {t('student_assignment.view_summary')}
                </BreakoutButton>
              )}
              {peerReviewController.userIsInPeerReview &&
              peerReviewController.peerReviewsGivenByCurrentUser.value?.size ? (
                <BreakoutButton
                  data-testid="view-peer-reviews-button"
                  kind="accent"
                  onClick={(e) => {
                    showDialog(
                      () => (
                        <PeerReviewViewDialog
                          controller={peerReviewController}
                          assignmentTitle={assignmentTitle}
                        />
                      ),
                      { trigger: e.currentTarget }
                    )
                  }}
                  className="inline-block rounded-2xl bg-core-secondary px-6 py-2 text-body-large whitespace-nowrap text-core-on-tertiary"
                >
                  {tt.student_assignment.view_peer_reviews()}
                </BreakoutButton>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  )
})
